import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefCallback } from 'react';

const TILT_QUERY = '(hover: hover) and (pointer: fine)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export interface CardTiltHandlers {
  ref: RefCallback<HTMLElement>;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * True when the touch landed on something the card scrolls itself (its own
 * description column, or the whole panel when a breakpoint makes it the
 * scroller). Tilting such a gesture writes a transform onto the scroller's own
 * ancestor chain mid-pan, which mobile browsers answer by dropping the scroll —
 * the card wobbles and the text barely moves. The scroll wins instead.
 */
function ownsTouchScroll(root: HTMLElement, target: EventTarget | null): boolean {
  let node: Element = target instanceof Element ? target : root;
  for (;;) {
    if (node instanceof HTMLElement && scrolls(node)) return true;
    if (node === root) return false;
    const parent = node.parentElement;
    if (parent === null) return false;
    node = parent;
  }
}

function scrolls(el: HTMLElement): boolean {
  const style = window.getComputedStyle?.(el);
  if (style === undefined) return false;
  const scrollsY = (style.overflowY === 'auto' || style.overflowY === 'scroll')
    && el.scrollHeight - el.clientHeight > 1;
  const scrollsX = (style.overflowX === 'auto' || style.overflowX === 'scroll')
    && el.scrollWidth - el.clientWidth > 1;
  return scrollsY || scrollsX;
}

function reset(el: HTMLElement): void {
  el.style.setProperty('--cards-tilt-rx', '0deg');
  el.style.setProperty('--cards-tilt-ry', '0deg');
  el.style.setProperty('--cards-tilt-glow', '0');
  el.style.setProperty('--cards-tilt-nx', '0');
  el.style.setProperty('--cards-tilt-ny', '0');
  el.removeAttribute('data-tilting');
  el.removeAttribute('data-tilt-input');
}

/**
 * Balatro-style pointer tilt + glare for a card element. Writes the pointer
 * position into CSS custom properties (rotation + glare origin) via rAF; the
 * actual transform/shine lives in CSS.
 *
 * A fine hover pointer tilts continuously as the cursor moves. On touch, the
 * press tilts toward the finger and keeps following it while it slides,
 * returning on release. A touch that starts on a part the card scrolls itself is
 * left alone so the scroll gesture survives.
 * Reduced motion turns both paths off here and flattens them in CSS (03 §6).
 * Spread the returned handlers on the card root (which must carry `cards-tilt`).
 */
export function useCardTilt(maxDeg = 9): CardTiltHandlers {
  const frame = useRef<number | undefined>(undefined);
  const activeTouchPointer = useRef<number | undefined>(undefined);
  const tiltElement = useRef<HTMLElement | undefined>(undefined);
  const [enabled, setEnabled] = useState(false);
  const reducedMotion = useRef(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const hover = window.matchMedia(TILT_QUERY);
    const motion = window.matchMedia(REDUCED_MOTION_QUERY);
    const update = (): void => {
      reducedMotion.current = motion.matches;
      setEnabled(hover.matches && !motion.matches);
    };
    update();
    hover.addEventListener?.('change', update);
    motion.addEventListener?.('change', update);
    return () => {
      hover.removeEventListener?.('change', update);
      motion.removeEventListener?.('change', update);
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
      if (tiltElement.current !== undefined) reset(tiltElement.current);
    };
  }, []);

  const applyTilt = useCallback((
    el: HTMLElement,
    clientX: number,
    clientY: number,
    input: 'pointer' | 'tap' = 'pointer',
  ): void => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const px = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const py = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    const degrees = input === 'tap' ? Math.min(maxDeg, 6) : maxDeg;
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      el.style.setProperty('--cards-tilt-ry', `${(px - 0.5) * 2 * degrees}deg`);
      el.style.setProperty('--cards-tilt-rx', `${-(py - 0.5) * 2 * degrees}deg`);
      el.style.setProperty('--cards-glare-x', `${px * 100}%`);
      el.style.setProperty('--cards-glare-y', `${py * 100}%`);
      // Where the pointer is, -1..1: the art drifts that way (see KIT_CSS).
      el.style.setProperty('--cards-tilt-nx', ((px - 0.5) * 2).toFixed(3));
      el.style.setProperty('--cards-tilt-ny', ((py - 0.5) * 2).toFixed(3));
      el.style.setProperty('--cards-tilt-glow', '1');
      el.setAttribute('data-tilt-input', input);
      el.setAttribute('data-tilting', 'true');
    });
  }, [maxDeg]);

  const ref = useCallback<RefCallback<HTMLElement>>((el) => {
    tiltElement.current = el ?? undefined;
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    if (event.pointerType === 'touch') {
      // Only the pointer that took the press keeps tilting: a gesture the card
      // handed to a scroller never registered one, and the
      // browser's own pointercancel ends the follow as soon as it takes over.
      if (activeTouchPointer.current !== event.pointerId) return;
      applyTilt(event.currentTarget, event.clientX, event.clientY, 'tap');
      return;
    }
    if (!enabled) return;
    applyTilt(event.currentTarget, event.clientX, event.clientY);
  }, [applyTilt, enabled]);

  const onPointerLeave = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    if (event.pointerType === 'touch') return;
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    reset(event.currentTarget);
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    if (event.pointerType === 'touch') {
      const el = event.currentTarget;
      if (reducedMotion.current || ownsTouchScroll(el, event.target)) {
        activeTouchPointer.current = undefined;
        return;
      }
      activeTouchPointer.current = event.pointerId;
      applyTilt(el, event.clientX, event.clientY, 'tap');
      return;
    }
    // A fine pointer flattens on press so a click never fires mid-tilt.
    reset(event.currentTarget);
  }, [applyTilt]);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    if (event.pointerType !== 'touch' || activeTouchPointer.current !== event.pointerId) return;
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    activeTouchPointer.current = undefined;
    reset(event.currentTarget);
  }, []);

  const onPointerCancel = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    if (event.pointerType === 'touch' && activeTouchPointer.current !== event.pointerId) return;
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    activeTouchPointer.current = undefined;
    reset(event.currentTarget);
  }, []);

  return { ref, onPointerMove, onPointerLeave, onPointerDown, onPointerUp, onPointerCancel };
}
